import { scopedProjectKey, scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { EnvironmentId, ProjectId, ScopedProjectRef } from "@cmmd-forge/contracts";

import { deriveLogicalProjectKeyMap } from "~/logicalProject";
import type { Project } from "~/types";
import type { ForgeExecutionTarget } from "./forgeExecutionTarget";
import type { ProjectWorkSessionsState } from "./useProjectWorkSessions";

interface ProjectEnvironmentState {
  readonly projectIds: readonly ProjectId[];
  readonly projectById: Readonly<Record<ProjectId, Project>>;
}

export function resolveForgeProjectRemovalRefs(
  environmentStateById: Readonly<Record<string, ProjectEnvironmentState>>,
  seedRefs: readonly ScopedProjectRef[],
): readonly ScopedProjectRef[] {
  const projects = Object.values(environmentStateById).flatMap((environmentState) =>
    environmentState.projectIds.flatMap((id) => {
      const project = environmentState.projectById[id];
      return project ? [project] : [];
    }),
  );
  const logicalKeyByPhysicalKey = deriveLogicalProjectKeyMap(projects);
  const seedLogicalKeys = new Set(
    seedRefs.flatMap((ref) => {
      const key = logicalKeyByPhysicalKey.get(scopedProjectKey(ref));
      return key ? [key] : [];
    }),
  );
  return projects.flatMap((project) => {
    const ref = scopeProjectRef(project.environmentId, project.id);
    const logicalKey = logicalKeyByPhysicalKey.get(scopedProjectKey(ref));
    return logicalKey && seedLogicalKeys.has(logicalKey) ? [ref] : [];
  });
}

export function projectRefsForRemoval(
  forgeTarget: ForgeExecutionTarget | null,
  allSessions: ProjectWorkSessionsState["allSessions"],
): readonly ScopedProjectRef[] {
  return [
    ...(forgeTarget
      ? [
          scopeProjectRef(
            forgeTarget.environmentId as EnvironmentId,
            forgeTarget.projectId as ProjectId,
          ),
        ]
      : []),
    ...allSessions.map((session) =>
      scopeProjectRef(
        session.forgeProject.environmentId as EnvironmentId,
        session.forgeProject.projectId as ProjectId,
      ),
    ),
  ];
}
